// IIFE-scoped so QR_OFFER_STORAGE_KEY can't collide with the same constant in
// cart-campaign-listener.js if both ever load on the same page. Keep the literal in sync.
(function () {
  const QR_OFFER_STORAGE_KEY = 'fiera:qr_offer';

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`${url} responded ${response.status}`);
      return response.json();
    });
  }

  if (customElements.get('cart-redirect')) return;
  customElements.define(
    'cart-redirect',
    class CartRedirect extends HTMLElement {
      connectedCallback() {
        this.redirectUrl = this.dataset.redirectUrl || '/';
        this.cartUpdateUrl = this.dataset.cartUpdateUrl || '/cart/update.js';
        this.config = this.parseConfig();

        this.run();
      }

      parseConfig() {
        const node = this.querySelector('.cart-redirect__config');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('cart-redirect: could not parse config', error);
          return null;
        }
      }

      async run() {
        try {
          const hasThresholds =
            this.config?.thresholdsByCurrency &&
            Object.keys(this.config.thresholdsByCurrency).length > 0;
          if (this.config?.campaignId && this.config.items?.length && hasThresholds) {
            this.writeLocalState();
            if (this.config.mode === 'immediate') {
              // Immediate mode sets both campaign attributes and adds the gift so the customer lands
              // with it already in the cart. The loader overlay masks these requests. If it fails,
              // the listener re-adds on the destination page, so we never block the redirect on it.
              try {
                await this.setupImmediateGift();
              } catch (error) {
                console.warn('cart-redirect: immediate gift add failed, listener will retry', error);
              }
            } else {
              await this.writeCartAttribute();
            }
          }
        } catch (error) {
          console.error('cart-redirect: enrollment failed', error);
        } finally {
          // Always redirect so the customer is never stranded on the blank page.
          window.location = this.safeRedirectUrl();
        }
      }

      safeRedirectUrl() {
        try {
          const url = new URL(this.redirectUrl, window.location.origin);
          if (url.protocol === 'http:' || url.protocol === 'https:') return url.toString();
        } catch (error) {
          console.warn('cart-redirect: invalid redirect URL, falling back to /', error);
        }
        return '/';
      }

      writeLocalState() {
        const state = {
          campaignId: this.config.campaignId,
          mode: this.config.mode === 'immediate' ? 'immediate' : 'listener',
          note: this.config.note || '',
          thresholds: this.config.thresholdsByCurrency,
          items: this.config.items,
          addedOnce: false,
          dismissed: false,
          enrolledAt: Date.now(),
        };
        try {
          localStorage.setItem(QR_OFFER_STORAGE_KEY, JSON.stringify(state));
        } catch (error) {
          // Private mode or quota — listener will no-op but the cart attribute still flips for the discount fn.
          console.warn('cart-redirect: could not write localStorage', error);
        }
      }

      writeCartAttribute() {
        return postJson(this.cartUpdateUrl, { attributes: { _qr_offer: this.config.campaignId } });
      }

      // Immediate mode: set both campaign attributes in one write, then add the gift. `_qr_threshold`
      // is written alongside `_qr_offer` *before* the gift is added, so the Shopify Functions discount
      // gates on it from the very first render (no window where the gift is briefly discounted below
      // threshold). Setting it here also means the listener's first reconcile on the destination finds
      // it already correct and won't re-write it. The listener keeps it in sync on later currency
      // switches.
      async setupImmediateGift() {
        const cid = this.config.campaignId;
        let cart;
        try {
          const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`/cart.js responded ${response.status}`);
          cart = await response.json();
        } catch (error) {
          // Couldn't read the cart for currency/dedup — still flip the campaign attribute so the
          // listener can take over on the destination page, then surface the failure.
          await this.writeCartAttribute();
          throw error;
        }
        const currency = (cart.currency || '').toUpperCase();
        const threshold = this.config.thresholdsByCurrency[currency];
        const attributes = { _qr_offer: cid };
        if (typeof threshold === 'number') attributes._qr_threshold = String(threshold);
        await postJson(this.cartUpdateUrl, { attributes });
        // Currency this campaign doesn't cover — `_qr_offer` is set, but no gift (mirrors the listener).
        if (typeof threshold !== 'number') return;
        // Don't double-add if this campaign's gift is already in the cart (e.g. a re-scan).
        const alreadyHasGift = (cart.items || []).some(
          (item) => item.properties && item.properties._qr_offer === cid
        );
        if (!alreadyHasGift) await this.addGiftItems();
      }

      // Same payload shape as cart-campaign-listener.js `addItems` — each gift line carries the
      // `_qr_offer` property the discount function matches against the cart attribute.
      addGiftItems() {
        const campaignId = this.config.campaignId;
        const payload = {
          items: this.config.items.map((item) => ({
            ...item,
            properties: { ...(item.properties || {}), _qr_offer: campaignId },
          })),
        };
        return postJson('/cart/add.js', payload);
      }
    }
  );
})();
